import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet'
import { useRenameCategory, useToggleCategoryHidden, useUpdateCategoryColor, useSaveBudgetPair } from '@/lib/hooks'
import type { Category, Budget } from '@/lib/hooks'
import { COLOR_PRESETS, DEFAULT_CATEGORY_COLOR } from '@/lib/constants'
import { formatAmount } from '@/lib/utils'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  category: Category | null
  householdBudget: Budget | undefined
  personalBudget: Budget | undefined
  userId: string
  onDelete: (category: Category) => void
  householdLocked?: boolean
  personalLocked?: boolean
  householdLockedAmount?: number
  personalLockedAmount?: number
}

export function ParentCategoryEditSheet({
  open,
  onOpenChange,
  category,
  householdBudget,
  personalBudget,
  userId,
  onDelete,
  householdLocked,
  personalLocked,
  householdLockedAmount,
  personalLockedAmount,
}: Props) {
  const [name, setName] = useState('')
  const [householdAmount, setHouseholdAmount] = useState('')
  const [personalAmount, setPersonalAmount] = useState('')
  const [colorPickerOpen, setColorPickerOpen] = useState(false)

  const renameCat = useRenameCategory()
  const toggleHidden = useToggleCategoryHidden()
  const updateColor = useUpdateCategoryColor()
  const saveBudgetPair = useSaveBudgetPair()

  useEffect(() => {
    if (open && category) {
      setName(category.name)
      setHouseholdAmount(householdBudget ? String(householdBudget.amount) : '')
      setPersonalAmount(personalBudget ? String(personalBudget.amount) : '')
      setColorPickerOpen(false)
    }
  }, [open, category, householdBudget, personalBudget])

  const handleSave = () => {
    if (!category) return

    const promises: Promise<void>[] = []

    if (name.trim() && name.trim() !== category.name) {
      promises.push(
        new Promise((resolve, reject) => {
          renameCat.mutate(
            { id: category.id, name: name.trim() },
            { onSuccess: () => resolve(), onError: reject },
          )
        })
      )
    }

    const hAmount = householdLocked ? (householdBudget?.amount ?? null) : (householdAmount.trim() ? Number(householdAmount) : null)
    const pAmount = personalLocked ? (personalBudget?.amount ?? null) : (personalAmount.trim() ? Number(personalAmount) : null)
    const hChanged = !householdLocked && hAmount !== (householdBudget?.amount ?? null)
    const pChanged = !personalLocked && pAmount !== (personalBudget?.amount ?? null)

    if (hChanged || pChanged) {
      promises.push(
        new Promise((resolve, reject) => {
          saveBudgetPair.mutate(
            {
              household: { budgets: [{ category_id: category.id, amount: hAmount }] },
              personal: { budgets: [{ category_id: category.id, amount: pAmount }], userId },
            },
            { onSuccess: () => resolve(), onError: reject },
          )
        })
      )
    }

    Promise.all(promises).then(() => onOpenChange(false))
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" onOpenAutoFocus={(e) => e.preventDefault()}>
        <SheetHeader>
          <SheetTitle>カテゴリを編集</SheetTitle>
          <SheetDescription>
            カテゴリの名前・色・予算を変更できます。
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 px-4">
          <div className="space-y-2">
            <Label htmlFor="parent-category-name">カテゴリ名</Label>
            <Input
              id="parent-category-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoCapitalize="off"
              enterKeyHint="done"
            />
          </div>

          <div className="space-y-2">
            <Label>色</Label>
            <button
              type="button"
              className="flex items-center gap-2"
              onClick={() => setColorPickerOpen(!colorPickerOpen)}
            >
              <div
                className="h-8 w-8 rounded-full border-2 border-foreground"
                style={{ backgroundColor: category?.color ?? DEFAULT_CATEGORY_COLOR }}
              />
              <span className="text-sm text-muted-foreground">
                {colorPickerOpen ? "閉じる" : "タップして変更"}
              </span>
            </button>
            {colorPickerOpen && (
              <div className="grid grid-cols-5 gap-2 pt-1">
                {COLOR_PRESETS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`size-9 rounded-full border-2 transition-transform hover:scale-110 mx-auto ${category?.color === color ? 'border-foreground' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => {
                      if (!category) return
                      updateColor.mutate(
                        { id: category.id, color },
                        { onSuccess: () => setColorPickerOpen(false) },
                      )
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="parent-budget-household">予算（家計）</Label>
            {householdLocked ? (
              <div className="space-y-1">
                <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                  {formatAmount(householdLockedAmount ?? 0)}（サブカテゴリ合計）
                </div>
                <p className="text-xs text-muted-foreground">
                  サブカテゴリに個別予算が設定されています。
                </p>
              </div>
            ) : (
              <CurrencyInput
                id="parent-budget-household"
                value={householdAmount}
                onChange={(e) => setHouseholdAmount(e.target.value)}
                placeholder="未設定"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="parent-budget-personal">予算（個人）</Label>
            {personalLocked ? (
              <div className="space-y-1">
                <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                  {formatAmount(personalLockedAmount ?? 0)}（サブカテゴリ合計）
                </div>
                <p className="text-xs text-muted-foreground">
                  サブカテゴリに個別予算が設定されています。
                </p>
              </div>
            ) : (
              <CurrencyInput
                id="parent-budget-personal"
                value={personalAmount}
                onChange={(e) => setPersonalAmount(e.target.value)}
                placeholder="未設定"
              />
            )}
          </div>
        </div>
        <SheetFooter>
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleSave}
              disabled={!name.trim() || renameCat.isPending || saveBudgetPair.isPending}
            >
              {(renameCat.isPending || saveBudgetPair.isPending) ? '保存中...' : '保存'}
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  if (!category) return
                  toggleHidden.mutate(
                    { id: category.id, isHidden: !category.is_hidden },
                    { onSuccess: () => onOpenChange(false) },
                  )
                }}
              >
                {category?.is_hidden ? "表示する" : "非表示にする"}
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => {
                  if (!category) return
                  onDelete(category)
                  onOpenChange(false)
                }}
              >
                削除
              </Button>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
